<script setup>
import { computed } from 'vue'
import { marked } from 'marked'
import { withTrailingSlash } from '@/utils/siteUrl'

const props = defineProps({
  block: { type: Object, required: true },
})

// Heading level → tag + size class. Sizes map Figma's type scale onto the
// nearest project tokens: L1 48 → h1, L2 32 → h2 (36), L3 22 → $font-size-h4,
// L4 18 → $font-size-lg (20). See _variables.scss.
const level = computed(() => props.block.headingLevel || 2)
const headingTag = computed(() => `h${Math.min(Math.max(level.value, 1), 4)}`)

// Parse the markdown body, then make any absolute (http/https) link open in a
// new tab — matching the site convention for external links. Relative in-app
// links are left as plain same-tab anchors, but normalized to carry the
// trailing slash the host's redirect expects (GSC indexing fix, 2026-09-24).
const bodyHtml = computed(() =>
  marked.parse(props.block.body || '')
    .replace(
      /<a href="(https?:\/\/[^"]+)"/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer"'
    )
    .replace(
      /<a href="(\/[^"]*)"/g,
      (match, href) => `<a href="${withTrailingSlash(href)}"`
    )
)

const isExternal = (to) => typeof to === 'string' && /^https?:\/\//.test(to)
</script>

<template>
  <div class="rich-text">
    <component :is="headingTag" v-if="block.heading" class="heading" :class="`lvl-${level}`">
      {{ block.heading }}
    </component>
    <p v-if="block.subheading" class="subheading">{{ block.subheading }}</p>

    <div v-if="block.body" class="prose" :class="{ 'link-list': block.linkList }" v-html="bodyHtml" />

    <a
      v-if="block.button && isExternal(block.button.to)"
      class="btn primary pill"
      :href="block.button.to"
      target="_blank"
      rel="noopener"
    >
      {{ block.button.label }} <span aria-hidden="true">→</span>
    </a>
    <RouterLink
      v-else-if="block.button"
      class="btn primary pill"
      :to="withTrailingSlash(block.button.to)"
    >
      {{ block.button.label }} <span aria-hidden="true">→</span>
    </RouterLink>

    <a
      v-if="block.link && isExternal(block.link.to)"
      class="secondary-link"
      :href="block.link.to"
      target="_blank"
      rel="noopener"
    >
      {{ block.link.label }} <span class="arrow" aria-hidden="true">→</span>
    </a>
    <RouterLink
      v-else-if="block.link"
      class="secondary-link"
      :to="withTrailingSlash(block.link.to)"
    >
      {{ block.link.label }} <span class="arrow" aria-hidden="true">→</span>
    </RouterLink>
  </div>
</template>

<style lang="scss" scoped>
.rich-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: $space-4;
  color: var(--color-ink);

  .heading {
    margin: 0;
    line-height: $lh-heading;
    letter-spacing: $ls-heading;
    font-weight: 500;

    &.lvl-1 { font-size: $font-size-h1; font-weight: 700; }
    &.lvl-2 { font-size: $font-size-h2; }
    &.lvl-3 { font-size: $font-size-h4; line-height: $lh-heading-sm; }
    &.lvl-4 { font-size: $font-size-lg; line-height: $lh-heading-sm; }
  }

  // Subheading sits directly under the heading — tighten the gap between them.
  .heading + .subheading { margin-top: -$space-2; }

  .subheading {
    margin: 0;
    font-size: $font-size-h4;
    font-weight: 500;
    line-height: $lh-heading-sm;
    color: var(--color-ink);
  }

  // Rendered markdown. Scoped styles don't reach v-html, so target via :deep().
  .prose {
    font-size: $font-size-base;
    line-height: $lh-base;
    color: var(--color-ink);

    :deep(p)  { margin: 0 0 $space-4; &:last-child { margin-bottom: 0; } }
    :deep(ul), :deep(ol) {
      margin: 0 0 $space-4 $space-5;
      padding-left: 0px;
      > li > ul, ol { // Nested lists: reset the margin/padding to the default browser indent.
        list-style-type: "–   ";
        li:first-child { margin-top: $space-2; }

      }
      &:last-child { margin-bottom: 0; } }
    :deep(li) { margin-bottom: $space-2; }

    // Link-list variant (e.g. "Further reading") — flush left under the block
    // heading / bold sub-labels, no bullet markers or indent.
    &.link-list :deep(ul) {
      list-style: none;
      margin-left: 0;
      padding-left: 0;
    }
    :deep(strong) { font-weight: 600; }
    :deep(a) {
      color: var(--color-primary);
      text-decoration: underline;
      text-underline-offset: 2px;
      &:hover { color: var(--color-primary-press); }
    }
    :deep(h2), :deep(h3) {
      margin: $space-5 0 $space-3;
      line-height: $lh-heading;
      letter-spacing: $ls-heading;
    }
    :deep(h4) {
      margin: $space-5 0 $space-3;
      line-height: $lh-heading-sm;
      letter-spacing: $ls-heading;
    }
    :deep(img) {
      display: block;
      max-width: 100%;
      height: auto;
      border-radius: $radius;
      margin: $space-4 0;
    }
  }

  // Large primary CTA — same blue pill as the home hero/impact buttons.
  .btn.pill {
    border-radius: $radius-pill;
    color: var(--color-bg);
    &:hover:not(:disabled) { color: var(--color-bg); }
  }

  // Secondary link — underlined dark link + arrow (mirrors ImpactCard's .link).
  .secondary-link {
    display: inline-flex;
    align-items: center;
    gap: $space-1;
    font-weight: 600;
    color: var(--color-ink);
    text-decoration: underline;
    text-underline-offset: 3px;
    @include transition(opacity, transform);

    .arrow { line-height: 1; }

    &:hover          { opacity: .75; }
    &:focus-visible  { @include focus-ring; outline: none; }
    &:active         { transform: translateY(1px); }
    &[aria-disabled] { opacity: .4; cursor: not-allowed; pointer-events: none; }
  }
}
</style>
